import React, { useEffect, useMemo, useRef, useState } from "react";
import { buildApiUrl, useBaseUrl } from "../../utils/utils";
import localforage from "localforage";

interface AssistantMessage {
    role: 'user' | 'assistant';
    content: string;
    has_image?: boolean;
}

const InstitutionAIAssistant: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [open, setOpen] = useState(false);
    const [connected, setConnected] = useState<boolean | null>(null);
    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [prompt, setPrompt] = useState('');
    const [busy, setBusy] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    
    // Auth info
    const [staffId, setStaffId] = useState<string>('');
    const [institutionId, setInstitutionId] = useState<string>('');
    const [staffName, setStaffName] = useState<string>('');

    const abortControllerRef = useRef<AbortController | null>(null);
    const timerRef = useRef<number | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    // Initial auth load
    useEffect(() => {
        const loadAuth = async () => {
            const sid = await localforage.getItem("staff_id") as string;
            const iid = await localforage.getItem("institution_id") as string;
            const fname = await localforage.getItem("fname") as string;
            if (sid) setStaffId(sid);
            if (iid) setInstitutionId(iid);
            if (fname) setStaffName(fname);
        };
        loadAuth();
    }, []);

    const defaultGreeting: AssistantMessage = { 
        role: 'assistant', 
        content: staffName 
            ? `Hi ${staffName}. I am Uzima AI, your healthcare collaboration assistant. I remember our past conversations! How can I help you today?`
            : `Hi. I am Uzima AI, your healthcare collaboration assistant. I remember our past conversations! How can I help you today?` 
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            const reader = new FileReader();
            reader.onload = (ev) => {
                setImagePreview(ev.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const canSend = useMemo(() => prompt.trim().length > 0 && !busy, [prompt, busy]);

    const readHeaders = (json = false) => ({
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
    });

    const readStatus = async () => {
        const response = await fetch(buildApiUrl(baseUrl, 'ai/ollama/status/'), { credentials: 'include', headers: readHeaders() });
        const payload = await response.json().catch(() => ({}));
        setConnected(Boolean(payload?.connected));
    };

    const loadHistory = async () => {
        if (!staffId || !institutionId) {
            setMessages([defaultGreeting]);
            return;
        }
        try {
            const response = await fetch(buildApiUrl(baseUrl, `ai/ollama/history/?staff_id=${staffId}&institution_id=${institutionId}`), { 
                credentials: 'include', 
                headers: readHeaders() 
            });
            const payload = await response.json();
            if (payload.messages && payload.messages.length > 0) {
                setMessages(payload.messages);
            } else {
                setMessages([defaultGreeting]);
            }
        } catch (err) {
            setMessages([defaultGreeting]);
        }
    };

    const clearHistory = async () => {
        if (!staffId || !institutionId) return;
        if (!window.confirm("Are you sure you want to clear your AI conversation history? This cannot be undone.")) return;
        
        try {
            await fetch(buildApiUrl(baseUrl, 'ai/ollama/history/clear/'), {
                method: 'POST',
                credentials: 'include',
                headers: readHeaders(true),
                body: JSON.stringify({ staff_id: staffId, institution_id: institutionId })
            });
            setMessages([defaultGreeting]);
        } catch (err) {
            console.error("Failed to clear history");
        }
    };

    const startTimer = () => {
        setElapsedSeconds(0);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = window.setInterval(() => {
            setElapsedSeconds((prev) => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setElapsedSeconds(0);
    };

    const handleStop = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        stopTimer();
        setBusy(false);
        setErrorText('');
        setMessages((prev) => [...prev, { role: 'assistant', content: 'Response cancelled by user.' }]);
    };

    const formatElapsed = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins > 0) return `${mins}m ${secs}s`;
        return `${secs}s`;
    };

    const sendPrompt = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSend) {
            return;
        }
        let base64Image = undefined;
        if (imageFile && imagePreview) {
            base64Image = imagePreview.split(',')[1];
        }
        
        const userMessage: any = { role: 'user' as const, content: prompt.trim() };
        if (base64Image) {
            userMessage.images = [base64Image];
            userMessage.has_image = true;
        }
        
        const nextMessages = [...messages, userMessage];
        setMessages(nextMessages);
        setBusy(true);
        setErrorText('');
        setPrompt('');
        setImageFile(null);
        setImagePreview(null);

        const controller = new AbortController();
        abortControllerRef.current = controller;
        startTimer();

        // Only send the current prompt context to the API. 
        // The backend memory will fetch the past history context automatically.
        const currentExchange = [userMessage];

        try {
            const response = await fetch(buildApiUrl(baseUrl, 'ai/ollama/chat/'), {
                method: 'POST',
                credentials: 'include',
                headers: readHeaders(true),
                body: JSON.stringify({ 
                    messages: currentExchange,
                    staff_id: staffId,
                    institution_id: institutionId
                }),
                signal: controller.signal
            });
            const payload = await response.json().catch(() => ({}));
            stopTimer();
            setBusy(false);
            abortControllerRef.current = null;
            if (!response.ok) {
                setErrorText(payload?.error || 'AI assistant is unavailable right now.');
                setMessages(nextMessages);
                return;
            }
            setMessages([...nextMessages, { role: 'assistant', content: payload?.message?.content || 'No response returned.' }]);
        } catch (err: any) {
            stopTimer();
            abortControllerRef.current = null;
            setBusy(false);
            if (err.name === 'AbortError') {
                return;
            }
            setErrorText('Connection to AI assistant failed. Please try again.');
        }
    };

    useEffect(() => {
        if (open) {
            readStatus().catch(() => {
                setConnected(false);
            });
            // Only load history if it hasn't been loaded yet
            if (messages.length === 0 && staffId) {
                loadHistory();
            } else if (messages.length === 0) {
                setMessages([defaultGreeting]);
            }
        }
    }, [open, staffId]);

    // Update greeting if staffName loads after initial mount and no history exists
    useEffect(() => {
        if (messages.length === 1 && messages[0].content.startsWith('Hi.') && staffName) {
            setMessages([defaultGreeting]);
        }
    }, [staffName]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, busy]);

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    return (
        <div style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1060 }}>
            {open ? (
                <div className="card shadow-lg" style={{ width: 360, maxWidth: 'calc(100vw - 32px)' }}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <div>
                            <h6 className="mb-0">Uzima AI Assistant</h6>
                            <small className="text-muted">{connected === null ? 'Checking assistant status...' : connected ? 'Ready & Learning' : 'AI assistant offline'}</small>
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearHistory} title="Clear memory">
                                <i className="bx bx-trash"></i>
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(false)}>Close</button>
                        </div>
                    </div>
                    <div className="card-body d-flex flex-column gap-3">
                        <div className="border rounded p-3 bg-light-subtle" style={{ minHeight: 260, maxHeight: 260, overflowY: 'auto' }}>
                            {messages.map((item, index) => (
                                <div key={`${item.role}-${index}`} className={`d-flex mb-3 ${item.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                                    <div className={`rounded p-2 ${item.role === 'user' ? 'bg-primary text-white' : 'bg-white border'}`} style={{ maxWidth: '85%', whiteSpace: 'pre-wrap' }}>
                                        {item.has_image && item.role === 'user' && (
                                            <div className="mb-2 small text-white-50"><i className="bx bx-image"></i> [Image attached]</div>
                                        )}
                                        {item.content}
                                    </div>
                                </div>
                            ))}
                            {busy && (
                                <div className="d-flex justify-content-start mb-3">
                                    <div className="rounded p-2 bg-white border" style={{ maxWidth: '85%' }}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <span className="text-muted small">Thinking... {formatElapsed(elapsedSeconds)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        {errorText ? <div className="alert alert-danger py-2 mb-0">{errorText}</div> : null}
                        
                        {imagePreview && (
                            <div className="position-relative d-inline-block mb-2 align-self-start">
                                <img src={imagePreview} alt="Preview" style={{ height: 60, borderRadius: 8, objectFit: 'cover' }} />
                                <button type="button" className="btn btn-sm btn-danger rounded-circle position-absolute top-0 start-100 translate-middle p-0" style={{ width: 20, height: 20, lineHeight: 1 }} onClick={() => { setImageFile(null); setImagePreview(null); }}>
                                    &times;
                                </button>
                            </div>
                        )}
                        
                        <form className="d-flex flex-column gap-2" onSubmit={sendPrompt}>
                            <textarea className="form-control" rows={3} value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="Ask about healthcare, patients, or clinic ops..." />
                            <div className="d-flex gap-2">
                                <label className="btn btn-outline-secondary mb-0 d-flex align-items-center justify-content-center" style={{ width: 40, cursor: 'pointer' }}>
                                    <i className="bx bx-image-add fs-5"></i>
                                    <input type="file" accept="image/*" className="d-none" onChange={handleImageChange} />
                                </label>
                                {busy ? (
                                    <button type="button" className="btn btn-danger flex-grow-1 d-flex align-items-center justify-content-center gap-2" onClick={handleStop}>
                                        <i className="bx bx-stop-circle fs-5"></i>
                                        Stop ({formatElapsed(elapsedSeconds)})
                                    </button>
                                ) : (
                                    <button type="submit" className="btn btn-dark flex-grow-1" disabled={!canSend}>Send to AI</button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
            <button type="button" className="btn btn-primary rounded-circle shadow-lg" style={{ width: 60, height: 60 }} onClick={() => setOpen((current) => !current)}>
                <i className="bx bx-bot fs-4"></i>
            </button>
        </div>
    );
};

export default InstitutionAIAssistant;
